import React, { useState, useEffect, useCallback } from "react"
import { Link, useParams, useHistory } from "react-router-dom"
import Back from "../common/Back"
import ListingCard from "./ListingCard"
import SignInPrompt from "../common/SignInPrompt"
import { API_BASE, authFetch } from "../../lib/api"
import { useAuth } from "../../context/AuthContext"
import "./listingDetail.css"
import "./listings.css"

const defaultMessage = (listing) =>
  `I am interested in ${listing.name} at ${listing.location}`

const ListingDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const { user } = useAuth()
  const [listing, setListing] = useState(null)
  const [relatedListings, setRelatedListings] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [galleryIndex, setGalleryIndex] = useState(0)
  const [savedListingIds, setSavedListingIds] = useState([])
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  })
  const [inquiryLoading, setInquiryLoading] = useState(false)
  const [inquirySuccess, setInquirySuccess] = useState(false)
  const [inquiryError, setInquiryError] = useState("")
  const [showSignInPrompt, setShowSignInPrompt] = useState(false)
  const [signInPromptMessage, setSignInPromptMessage] = useState("Sign in to continue.")
  const [showApplyModal, setShowApplyModal] = useState(false)
  const [applyForm, setApplyForm] = useState({ message: "", income: "", employer: "" })
  const [applyLoading, setApplyLoading] = useState(false)
  const [applySuccess, setApplySuccess] = useState(false)
  const [applyError, setApplyError] = useState("")
  const [showBookModal, setShowBookModal] = useState(false)
  const [bookForm, setBookForm] = useState({ type: "nightly", startDate: "", endDate: "", notes: "" })
  const [bookLoading, setBookLoading] = useState(false)
  const [bookSuccess, setBookSuccess] = useState(false)
  const [bookError, setBookError] = useState("")

  useEffect(() => {
    if (!id) {
      setNotFound(true)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setNotFound(false)
    fetch(`${API_BASE}/api/listings/${id}`)
      .then((res) => {
        if (res.status === 404) return null
        return res.json()
      })
      .then((data) => {
        if (cancelled) return
        if (data == null || data.error) {
          setNotFound(true)
          setListing(null)
        } else {
          setListing(data)
          setFormData((prev) => ({ ...prev, message: defaultMessage(data) }))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setNotFound(true)
          setListing(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [id])

  useEffect(() => {
    if (!listing || !id) return
    let cancelled = false
    fetch(`${API_BASE}/api/listings?limit=10`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled || !data.listings) return
        const related = data.listings
          .filter((item) => String(item.id) !== String(id))
          .slice(0, 3)
        setRelatedListings(related)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [listing, id])

  useEffect(() => {
    if (!user) {
      setSavedListingIds([])
      return
    }
    authFetch("/api/saved-homes")
      .then((r) => r.json())
      .then((data) => {
        if (data.savedHomes) setSavedListingIds(data.savedHomes.map((h) => h.listingId))
      })
      .catch(() => {})
  }, [user])

  const isSaved = listing && savedListingIds.includes(listing.id)

  const handleToggleSave = useCallback(() => {
    if (!user) {
      setSignInPromptMessage("Sign in to save this home to My List.")
      setShowSignInPrompt(true)
      return
    }
    if (!listing) return
    if (isSaved) {
      authFetch(`/api/saved-homes/${listing.id}`, { method: "DELETE" }).then((r) => {
        if (r.ok) setSavedListingIds((prev) => prev.filter((lid) => lid !== listing.id))
      })
    } else {
      authFetch("/api/saved-homes", { method: "POST", body: JSON.stringify({ listingId: listing.id }) })
        .then((r) => r.json())
        .then((data) => {
          if (data.listingId && !savedListingIds.includes(data.listingId))
            setSavedListingIds((prev) => [...prev, data.listingId])
        })
    }
  }, [user, listing, isSaved, savedListingIds])

  const handleOpenApply = () => {
    if (!user) {
      setSignInPromptMessage("Sign in to apply for this rental.")
      setShowSignInPrompt(true)
      return
    }
    setApplyForm({ message: "", income: "", employer: "" })
    setApplySuccess(false)
    setApplyError("")
    setShowApplyModal(true)
  }

  const handleApplySubmit = async (e) => {
    e.preventDefault()
    setApplyLoading(true)
    setApplyError("")
    try {
      const res = await authFetch("/api/rental-applications", {
        method: "POST",
        body: JSON.stringify({
          listingId: listing.id,
          formData: { message: applyForm.message, income: applyForm.income, employer: applyForm.employer },
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setApplyError(data.error || "Something went wrong")
        return
      }
      setApplySuccess(true)
    } catch (err) {
      setApplyError("Could not submit. Please try again.")
    } finally {
      setApplyLoading(false)
    }
  }

  const handleOpenBook = () => {
    if (!user) {
      setSignInPromptMessage("Sign in to book a viewing.")
      setShowSignInPrompt(true)
      return
    }
    setBookForm({ type: "nightly", startDate: "", endDate: "", notes: "" })
    setBookSuccess(false)
    setBookError("")
    setShowBookModal(true)
  }

  const handleBookSubmit = async (e) => {
    e.preventDefault()
    setBookLoading(true)
    setBookError("")
    try {
      const res = await authFetch("/api/bookings", {
        method: "POST",
        body: JSON.stringify({
          listingId: listing.id,
          type: bookForm.type,
          startDate: bookForm.startDate,
          endDate: bookForm.endDate,
          notes: bookForm.notes || undefined,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setBookError(data.error || "Something went wrong")
        return
      }
      setBookSuccess(true)
    } catch (err) {
      setBookError("Could not create booking. Please try again.")
    } finally {
      setBookLoading(false)
    }
  }

  const handleToggleSaveRelated = useCallback(
    (listingId) => {
      if (!user) {
        setSignInPromptMessage("Sign in to save homes to My List.")
        setShowSignInPrompt(true)
        return
      }
      const saved = savedListingIds.includes(listingId)
      if (saved) {
        authFetch(`/api/saved-homes/${listingId}`, { method: "DELETE" }).then((r) => {
          if (r.ok) setSavedListingIds((prev) => prev.filter((lid) => lid !== listingId))
        })
      } else {
        authFetch("/api/saved-homes", { method: "POST", body: JSON.stringify({ listingId }) })
          .then((r) => r.json())
          .then((data) => {
            if (data.listingId && !savedListingIds.includes(data.listingId))
              setSavedListingIds((prev) => [...prev, data.listingId])
          })
      }
    },
    [user, savedListingIds]
  )

  const gallery = listing?.gallery?.length ? listing.gallery : (listing?.cover ? [listing.cover] : [])

  if (loading) {
    return (
      <div className='listing-detail listing-detail--not-found'>
        <div className='container'>
          <p>Loading…</p>
        </div>
      </div>
    )
  }

  if (notFound || !listing) {
    return (
      <div className='listing-detail listing-detail--not-found'>
        <div className='container'>
          <p>This listing could not be found.</p>
          <Link to='/listings'>Back to all listings</Link>
        </div>
      </div>
    )
  }

  const isForRent = listing.category === "For Rent"

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setInquiryError("")
    setInquirySuccess(false)
    setInquiryLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          listingId: listing.id,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setInquiryError(data.error || "Something went wrong")
        return
      }
      setInquirySuccess(true)
    } catch (err) {
      setInquiryError("Could not send. Please try again.")
    } finally {
      setInquiryLoading(false)
    }
  }

  return (
    <div className='listing-detail'>
      <Back name='Listing' title={listing.name} cover={listing.cover} />

      <div className='container'>
        <nav className='listing-detail__breadcrumb'>
          <Link to='/listings'>Listings</Link>
          <span className='listing-detail__breadcrumb-sep'>/</span>
          <span>{listing.name}</span>
        </nav>

        <div className='listing-detail__main'>
          <div className='listing-detail__main-left'>
            <div className='listing-detail__gallery'>
              <div className='listing-detail__gallery-main'>
                <img src={gallery[galleryIndex]} alt={listing.name} />
                {gallery.length > 1 && (
                  <>
                    <button
                      type='button'
                      className='listing-detail__gallery-nav listing-detail__gallery-nav--prev'
                      onClick={() => setGalleryIndex((i) => (i === 0 ? gallery.length - 1 : i - 1))}
                      aria-label='Previous image'
                    >
                      <i className='fa fa-chevron-left'></i>
                    </button>
                    <button
                      type='button'
                      className='listing-detail__gallery-nav listing-detail__gallery-nav--next'
                      onClick={() => setGalleryIndex((i) => (i === gallery.length - 1 ? 0 : i + 1))}
                      aria-label='Next image'
                    >
                      <i className='fa fa-chevron-right'></i>
                    </button>
                  </>
                )}
              </div>
              {gallery.length > 1 && (
                <div className='listing-detail__gallery-thumbs'>
                  {gallery.map((src, i) => (
                    <button
                      key={i}
                      type='button'
                      className={`listing-detail__thumb ${i === galleryIndex ? "listing-detail__thumb--active" : ""}`}
                      onClick={() => setGalleryIndex(i)}
                    >
                      <img src={src} alt={`View ${i + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className='listing-detail__content'>
              <div className='listing-detail__header'>
                <span
                  className='listing-detail__badge'
                  style={{
                    background: listing.category === "For Sale" ? "#25b5791a" : "#ff98001a",
                    color: listing.category === "For Sale" ? "#25b579" : "#ff9800",
                  }}
                >
                  {listing.category}
                </span>
                <h2 className='listing-detail__title'>{listing.name}</h2>
                <p className='listing-detail__location'>
                  <i className='fa fa-location-dot'></i> {listing.location}
                </p>
                <div className='listing-detail__price-wrap'>
                  <span className='listing-detail__price'>{listing.price}</span>
                  {isForRent && <span className='listing-detail__price-unit'>/month</span>}
                </div>
              </div>

              {(listing.beds != null || listing.baths != null || listing.area) && (
                <div className='listing-detail__specs'>
                  {listing.beds != null && (
                    <div className='listing-detail__spec'>
                      <i className='fa fa-bed'></i>
                      <span>{listing.beds} Beds</span>
                    </div>
                  )}
                  {listing.baths != null && (
                    <div className='listing-detail__spec'>
                      <i className='fa fa-bath'></i>
                      <span>{listing.baths} Baths</span>
                    </div>
                  )}
                  {listing.area && (
                    <div className='listing-detail__spec'>
                      <i className='fa fa-ruler-combined'></i>
                      <span>{listing.area}</span>
                    </div>
                  )}
                </div>
              )}

              <div className='listing-detail__meta'>
                <span><strong>Property type:</strong> {listing.type}</span>
              </div>

              {listing.description && (
                <div className='listing-detail__description'>
                  <h3>About this property</h3>
                  <p>{listing.description}</p>
                </div>
              )}

              <div className='listing-detail__actions'>
                <button
                  type='button'
                  className={`listing-detail__cta ${isSaved ? "listing-detail__cta--saved" : "listing-detail__cta--secondary"}`}
                  onClick={handleToggleSave}
                >
                  {isSaved ? (
                    <><i className='fa fa-heart' style={{ marginRight: 8 }} /> Saved to My List</>
                  ) : (
                    <><i className='fa fa-heart-o' style={{ marginRight: 8 }} /> Save to My List</>
                  )}
                </button>
                {isForRent && (
                  <button type='button' className='listing-detail__cta listing-detail__cta--primary' onClick={handleOpenApply}>
                    Apply to rent
                  </button>
                )}
                <button type='button' className='listing-detail__cta listing-detail__cta--primary' onClick={handleOpenBook}>
                  Book a viewing
                </button>
                <button
                  type='button'
                  className='listing-detail__cta listing-detail__cta--secondary'
                  onClick={() => history.goBack()}
                >
                  Back to listings
                </button>
              </div>
            </div>
          </div>

          <aside className='listing-detail__sidebar'>
            <div className='listing-detail__form-wrap'>
              <h3 className='listing-detail__form-title'>Inquire about this listing</h3>
              {inquirySuccess && <p className='listing-detail__form-success'>Thanks! We&apos;ve received your inquiry and will get back to you soon.</p>}
              {inquiryError && <p className='listing-detail__form-error'>{inquiryError}</p>}
              <form onSubmit={handleSubmit} className='listing-detail__form'>
                <label htmlFor='inquiry-name'>Name</label>
                <input
                  id='inquiry-name'
                  type='text'
                  name='name'
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder='Your name'
                  required
                />
                <label htmlFor='inquiry-phone'>Phone</label>
                <input
                  id='inquiry-phone'
                  type='tel'
                  name='phone'
                  value={formData.phone}
                  onChange={handleFormChange}
                  placeholder='Your phone number'
                  required
                />
                <label htmlFor='inquiry-email'>Email</label>
                <input
                  id='inquiry-email'
                  type='email'
                  name='email'
                  value={formData.email}
                  onChange={handleFormChange}
                  placeholder='Your email'
                  required
                />
                <label htmlFor='inquiry-message'>Message</label>
                <textarea
                  id='inquiry-message'
                  name='message'
                  value={formData.message}
                  onChange={handleFormChange}
                  placeholder='Your message'
                  rows={4}
                  required
                />
                <button type='submit' className='listing-detail__cta listing-detail__cta--primary' disabled={inquiryLoading}>
                  {inquiryLoading ? "Sending…" : "Inquire about this listing"}
                </button>
              </form>
            </div>
          </aside>
        </div>

        {relatedListings.length > 0 && (
          <section className='listing-detail__related'>
            <h2 className='listing-detail__related-title'>Check out related listings or homes</h2>
            <div className='listing-detail__related-grid'>
              {relatedListings.map((item) => (
                <ListingCard
                  key={item.id}
                  listing={item}
                  isSaved={savedListingIds.includes(item.id)}
                  onToggleSave={handleToggleSaveRelated}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {showSignInPrompt && (
        <SignInPrompt message={signInPromptMessage} onClose={() => setShowSignInPrompt(false)} />
      )}

      {showApplyModal && (
        <div className='quote-modal-overlay' onClick={() => setShowApplyModal(false)} aria-hidden='true'>
          <div className='quote-modal' onClick={(e) => e.stopPropagation()}>
            <div className='quote-modal__header'>
              <h3>Apply to rent — {listing?.name}</h3>
              <button type='button' className='quote-modal__close' onClick={() => setShowApplyModal(false)} aria-label='Close'>&times;</button>
            </div>
            {applySuccess ? (
              <div className='quote-modal__success'>
                <p>Application submitted. We&apos;ll review and get back to you.</p>
                <button type='button' className='btn5' onClick={() => setShowApplyModal(false)}>Close</button>
              </div>
            ) : (
              <form onSubmit={handleApplySubmit} className='quote-modal__form'>
                {applyError && <p className='quote-modal__error'>{applyError}</p>}
                <label htmlFor='apply-message'>Message</label>
                <textarea id='apply-message' value={applyForm.message} onChange={(e) => setApplyForm((p) => ({ ...p, message: e.target.value }))} rows={3} placeholder='Tell us about yourself and why you want to rent.' />
                <label htmlFor='apply-income'>Income (optional)</label>
                <input id='apply-income' type='text' value={applyForm.income} onChange={(e) => setApplyForm((p) => ({ ...p, income: e.target.value }))} placeholder='Annual or monthly' />
                <label htmlFor='apply-employer'>Employer (optional)</label>
                <input id='apply-employer' type='text' value={applyForm.employer} onChange={(e) => setApplyForm((p) => ({ ...p, employer: e.target.value }))} placeholder='Current employer' />
                <div className='quote-modal__actions'>
                  <button type='button' className='btn5' style={{ background: "#666" }} onClick={() => setShowApplyModal(false)}>Cancel</button>
                  <button type='submit' className='btn5' disabled={applyLoading}>{applyLoading ? "Submitting…" : "Submit application"}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {showBookModal && (
        <div className='quote-modal-overlay' onClick={() => setShowBookModal(false)} aria-hidden='true'>
          <div className='quote-modal' onClick={(e) => e.stopPropagation()}>
            <div className='quote-modal__header'>
              <h3>Book a viewing — {listing?.name}</h3>
              <button type='button' className='quote-modal__close' onClick={() => setShowBookModal(false)} aria-label='Close'>&times;</button>
            </div>
            {bookSuccess ? (
              <div className='quote-modal__success'>
                <p>Booking request sent. We&apos;ll confirm your viewing soon.</p>
                <button type='button' className='btn5' onClick={() => setShowBookModal(false)}>Close</button>
              </div>
            ) : (
              <form onSubmit={handleBookSubmit} className='quote-modal__form'>
                {bookError && <p className='quote-modal__error'>{bookError}</p>}
                <label htmlFor='book-type'>Duration type</label>
                <select id='book-type' value={bookForm.type} onChange={(e) => setBookForm((p) => ({ ...p, type: e.target.value }))}>
                  <option value='nightly'>Nightly</option>
                  <option value='weekly'>Weekly</option>
                  <option value='monthly'>Monthly</option>
                </select>
                <label htmlFor='book-start'>Start date</label>
                <input id='book-start' type='date' value={bookForm.startDate} onChange={(e) => setBookForm((p) => ({ ...p, startDate: e.target.value }))} required />
                <label htmlFor='book-end'>End date</label>
                <input id='book-end' type='date' value={bookForm.endDate} onChange={(e) => setBookForm((p) => ({ ...p, endDate: e.target.value }))} required />
                <label htmlFor='book-notes'>Notes (optional)</label>
                <textarea id='book-notes' value={bookForm.notes} onChange={(e) => setBookForm((p) => ({ ...p, notes: e.target.value }))} rows={2} placeholder='Special requests' />
                <div className='quote-modal__actions'>
                  <button type='button' className='btn5' style={{ background: "#666" }} onClick={() => setShowBookModal(false)}>Cancel</button>
                  <button type='submit' className='btn5' disabled={bookLoading}>{bookLoading ? "Submitting…" : "Request booking"}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ListingDetail
